/**
 * MCPConnectionsSettings — Settings → AI Connections
 *
 * Reuses the parent Settings.js orchestrator pattern (pure JSX, all state
 * lives here for self-containment because no parent handler is shared).
 * RULE: do NOT add the issued JWT / refresh tokens to local state.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Bot, Wifi, WifiOff, Loader2, ShieldOff, Clock, Users, Activity, Download, AlertTriangle, Server, Plus, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import MCPConnectWizard from '../../components/MCPConnectWizard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const diffMs = Date.now() - t;
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return iso;
  }
}

export default function MCPConnectionsSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState({});
  const [policy, setPolicy] = useState({ allow_manager: false, allow_staff: false });
  const [policyLoaded, setPolicyLoaded] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [providerStatus, setProviderStatus] = useState([]);
  const [wizardProvider, setWizardProvider] = useState(null);

  // The Settings.js wrapper restricts this card to owner/manager/super_admin.
  // Managers can SEE the policy but cannot change it.
  const { user } = useAuth();
  const userRole = user?.role || '';
  const canEditPolicy = userRole === 'owner' || userRole === 'super_admin';

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const [conn, pol, use, status] = await Promise.all([
        axios.get(`${API}/mcp/connections`, cfg),
        axios.get(`${API}/mcp/policy`, cfg).catch(() => ({ data: { allow_manager: false, allow_staff: false } })),
        axios.get(`${API}/mcp/tenant/usage`, cfg).catch(() => ({ data: null })),
        axios.get(`${API}/mcp/tenant/providers/status`, cfg).catch(() => ({ data: [] })),
      ]);
      setRows(Array.isArray(conn.data) ? conn.data : []);
      setPolicy({ allow_manager: !!pol.data?.allow_manager, allow_staff: !!pol.data?.allow_staff });
      setPolicyLoaded(true);
      setUsage(use.data || null);
      setProviderStatus(Array.isArray(status.data) ? status.data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load AI connections';
      if (e?.response?.status !== 403) toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ---------------------------------------------------------------------------
  // Live connection polling — only active while the Connect Wizard is open.
  // Reuses GET /api/mcp/connections (no new endpoint). Stops on:
  //   - provider row flips to connected
  //   - wizard closes (effect cleanup)
  //   - 120s elapsed (hard cap)
  // ---------------------------------------------------------------------------
  const [livePolling, setLivePolling] = useState(false);
  const [liveTimedOut, setLiveTimedOut] = useState(false);
  useEffect(() => {
    if (!wizardProvider) {
      setLivePolling(false);
      setLiveTimedOut(false);
      return undefined;
    }
    setLivePolling(true);
    setLiveTimedOut(false);
    const startedAt = Date.now();
    const POLL_MS = 4000;
    const MAX_MS = 120000;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (cancelled) return;
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/mcp/connections`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (Array.isArray(data)) {
          setRows(data);
          const row = data.find((r) => r.client_id === wizardProvider);
          if (row?.connected) {
            setLivePolling(false);
            return; // success — do not reschedule
          }
        }
      } catch {
        /* swallow — keep polling until timeout */
      }
      if (cancelled) return;
      if (Date.now() - startedAt >= MAX_MS) {
        setLivePolling(false);
        setLiveTimedOut(true);
        return;
      }
      timer = setTimeout(tick, POLL_MS);
    };

    timer = setTimeout(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      setLivePolling(false);
    };
  }, [wizardProvider]);

  const liveConnected = !!rows.find((r) => r.client_id === wizardProvider)?.connected;

  const savePolicy = async (next) => {
    if (!canEditPolicy) return;
    setSavingPolicy(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(`${API}/mcp/policy`, next, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPolicy({ allow_manager: !!data.allow_manager, allow_staff: !!data.allow_staff });
      toast.success('AI Connections policy updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  const exportTenantCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/mcp/tenant/audit-export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `mcp-tenant-audit-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('MCP audit CSV exported');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    }
  };

  const statusPill = (status) => {
    const map = {
      Operational: 'bg-emerald-100 text-emerald-700',
      'Issues detected': 'bg-amber-100 text-amber-700',
      'Temporarily paused': 'bg-red-100 text-red-700',
    };
    return <Badge className={`${map[status] || 'bg-slate-100 text-slate-600'} hover:${map[status] || ''}`}>{status}</Badge>;
  };

  const disconnect = async (clientId) => {
    if (!window.confirm(`Disconnect ${clientId}? This will immediately revoke all active tokens.`)) return;
    setRevoking((s) => ({ ...s, [clientId]: true }));
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.delete(`${API}/mcp/connections/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${clientId} disconnected (${data.revoked || 0} tokens revoked)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disconnect failed');
    } finally {
      setRevoking((s) => ({ ...s, [clientId]: false }));
    }
  };

  return (
    <Card data-settings-section="mcp_connections" data-testid="mcp-connections-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <Bot size={18} /> AI Connections
            </CardTitle>
            <CardDescription>
              Manage which AI assistants (ChatGPT, Claude) can access this
              tenant via the ReStaff MCP server. Disconnecting revokes access
              immediately.
            </CardDescription>
          </div>
          <Link
            to="/help/mcp"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-100 rounded-full px-3 py-1.5 shrink-0 transition-colors"
            data-testid="mcp-setup-guide-link"
          >
            <BookOpen size={13} />
            MCP Setup Guide
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {/* Policy toggles — owner sets who may connect AI */}
        {policyLoaded && (
          <div className="mb-4 border rounded-lg p-3 bg-slate-50">
            <div className="flex items-center gap-2 mb-2">
              <Users size={14} className="text-slate-600" />
              <div className="font-medium text-sm">Who can connect AI tools</div>
            </div>
            <div className="text-xs text-slate-500 mb-3">
              By default only the tenant owner can connect ChatGPT / Claude / Gemini. Toggle below to extend access.
              {!canEditPolicy && (
                <span className="ml-1 italic">Owner only — read-only here.</span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <label className="flex items-center gap-2 text-sm" data-testid="mcp-policy-allow-manager">
                <Switch
                  checked={policy.allow_manager}
                  disabled={!canEditPolicy || savingPolicy}
                  onCheckedChange={(v) => savePolicy({ allow_manager: !!v, allow_staff: policy.allow_staff })}
                />
                Allow Manager MCP access
              </label>
              <label className="flex items-center gap-2 text-sm" data-testid="mcp-policy-allow-staff">
                <Switch
                  checked={policy.allow_staff}
                  disabled={!canEditPolicy || savingPolicy}
                  onCheckedChange={(v) => savePolicy({ allow_manager: policy.allow_manager, allow_staff: !!v })}
                />
                Allow Staff MCP access
              </label>
            </div>
          </div>
        )}

        {/* Usage panel (Phase 5) */}
        {usage && (
          <div className="mb-4 border rounded-lg p-3" data-testid="mcp-tenant-usage">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Activity size={14} className="text-slate-600" />
                <div className="font-medium text-sm">MCP Usage</div>
              </div>
              <Button size="sm" variant="outline" onClick={exportTenantCsv} data-testid="mcp-tenant-export-csv">
                <Download size={12} className="mr-1" /> Export CSV
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="rounded bg-slate-50 p-2"><div className="text-slate-500">Today</div><div className="font-bold text-base">{usage.requests_today.toLocaleString()}</div></div>
              <div className="rounded bg-slate-50 p-2"><div className="text-slate-500">30 days</div><div className="font-bold text-base">{usage.requests_30d.toLocaleString()}</div></div>
              <div className="rounded bg-slate-50 p-2"><div className="text-slate-500">Providers</div><div className="font-bold text-base">{usage.connected_providers}</div></div>
              <div className="rounded bg-slate-50 p-2">
                <div className="text-slate-500">Last activity</div>
                <div className="text-slate-800">{usage.last_activity ? new Date(usage.last_activity).toLocaleString() : '—'}</div>
              </div>
            </div>
            {/* Most used tools */}
            {usage.most_used_tools?.length > 0 && (
              <div className="mt-3 text-xs">
                <div className="text-slate-500 mb-1">Most used MCP tools (30d):</div>
                <div className="flex flex-wrap gap-1.5">
                  {usage.most_used_tools.map(t => (
                    <span key={t.tool_name} className="font-mono bg-slate-100 px-2 py-1 rounded">
                      {t.tool_name} <span className="text-slate-500">×{t.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Quota / fair use */}
            <div className="mt-3">
              <div className="flex justify-between items-center text-xs mb-1">
                <span className="text-slate-500">Monthly fair-use quota</span>
                <span className="font-medium">
                  {usage.requests_month_to_date.toLocaleString()} / {usage.monthly_quota.toLocaleString()} ({usage.quota_used_pct}%)
                </span>
              </div>
              <div className="h-2 w-full bg-slate-200 rounded overflow-hidden">
                <div className={`h-full ${usage.quota_alert_level === 'exceeded' ? 'bg-red-600' : usage.quota_alert_level === 'warning_95' ? 'bg-red-400' : usage.quota_alert_level === 'warning_80' ? 'bg-amber-400' : 'bg-emerald-500'}`}
                     style={{ width: `${Math.min(100, usage.quota_used_pct)}%` }} />
              </div>
              {usage.quota_alert_level !== 'ok' && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <AlertTriangle size={12} className={usage.quota_alert_level === 'exceeded' ? 'text-red-600' : 'text-amber-600'} />
                  <span className={usage.quota_alert_level === 'exceeded' ? 'text-red-600' : 'text-amber-700'}>
                    {usage.quota_alert_level === 'exceeded'
                      ? `You've exceeded your fair-use quota (${usage.quota_used_pct}%). MCP remains available — no hard lock applied.`
                      : usage.quota_alert_level === 'warning_95'
                      ? `You're at ${usage.quota_used_pct}% of your monthly fair-use quota.`
                      : `You're approaching your monthly fair-use quota (${usage.quota_used_pct}%).`}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Provider status panel (Phase 5) */}
        {providerStatus.length > 0 && (
          <div className="mb-4 border rounded-lg p-3" data-testid="mcp-tenant-status-panel">
            <div className="flex items-center gap-2 mb-2">
              <Server size={14} className="text-slate-600" />
              <div className="font-medium text-sm">MCP Service Status</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {providerStatus.map((p) => (
                <div key={p.client_id} className="rounded border p-2"
                     data-testid={`mcp-tenant-status-${p.client_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{p.client_name}</div>
                    {statusPill(p.status)}
                  </div>
                  {p.detail && <div className="text-xs text-slate-500 mt-1">{p.detail}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-6">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-slate-500 py-6 text-sm">No AI clients configured.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div
                key={r.client_id}
                data-testid={`mcp-conn-row-${r.client_id}`}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border rounded-lg p-3"
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 ${r.connected ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {r.connected ? <Wifi size={18} /> : <WifiOff size={18} />}
                  </div>
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      {r.client_name}
                      {r.connected ? (
                        <Badge variant="default" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">Disconnected</Badge>
                      )}
                    </div>
                    {r.connected && (
                      <div className="mt-1 text-xs text-slate-500 space-y-0.5">
                        {r.scopes?.length > 0 && (
                          <div>
                            <span className="font-medium text-slate-600">Scopes:</span>{' '}
                            {r.scopes.join(', ')}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock size={12} />
                          Last used: {formatRelative(r.last_used_at)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  {r.connected ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => disconnect(r.client_id)}
                      disabled={revoking[r.client_id]}
                      data-testid={`mcp-conn-disconnect-${r.client_id}`}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      {revoking[r.client_id] ? (
                        <Loader2 size={14} className="mr-1 animate-spin" />
                      ) : (
                        <ShieldOff size={14} className="mr-1" />
                      )}
                      Disconnect
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setWizardProvider(r.client_id)}
                      data-testid={`mcp-conn-connect-${r.client_id}`}
                    >
                      <Plus size={14} className="mr-1" />
                      Connect {r.client_name.split(' ')[0]}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <MCPConnectWizard
        open={!!wizardProvider}
        providerId={wizardProvider}
        polling={livePolling}
        connected={liveConnected}
        timedOut={liveTimedOut}
        onClose={() => { setWizardProvider(null); load(); }}
      />
    </Card>
  );
}
